import { Plus, Repeat1 } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useState } from "react";

import type { DataTypeItem, Attribute } from "@/shared/partnerschema";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RuleFormData } from "./Updatebuilderschema";

interface PricingTableProps {
    dataType: DataTypeItem[];
    attribute: Attribute[];
    setAttrCode: (code: string) => void;
    openExpression: boolean;
    setOpenExpression: (open: boolean) => void;
    setExpressionData: (data: any) => void;
}

export default function ModifyPricing({
    dataType,
    attribute,
    setAttrCode,
    setOpenExpression,
    setExpressionData,
}: PricingTableProps) {
    const { watch, setValue } = useFormContext<RuleFormData>();
    const pricingRows = watch("pricingRows");
    const [addWarning, setAddWarning] = useState("");

    const handlePricingChange = (rowId: number, key: string, value: any) => {
        setValue(
            "pricingRows",
            pricingRows.map((r) =>
                r.id === rowId ? { ...r, [key]: value?.Name ? value.Name : value } : r
            )
        );
        if (key === "dataType2") {
            setAttrCode(value?.ID);
        }
    };

    const handleAddPricingRow = (rowId: number) => {
        const currentRow = pricingRows.find((r) => r.id === rowId);
        if (!currentRow) return;

        const missingFields: string[] = [];
        if (!currentRow.dataType) missingFields.push("Data Type");
        else if (!currentRow.attribute) missingFields.push("Attribute");

        if (currentRow.expressionType === "value") {
            if (!currentRow.expressionValue) missingFields.push("Expression Value");
        } else {
            if (!currentRow.dataType2) missingFields.push("Operand Data Type");
            else if (!currentRow.attribute2) missingFields.push("Operand Attribute");
        }

        if (missingFields.length > 0) {
            setAddWarning(`Please fill: ${missingFields.join(", ")}`);
            setTimeout(() => setAddWarning(""), 2000);
            return;
        }

        setValue(
            "pricingRows",
            pricingRows.map((r) =>
                r.id === rowId ? { ...r, isEditing: false, isNew: false } : r
            )
        );
    };

    const handleEditPricingRow = (rowId: number) => {
        const currentlyEditingRow = pricingRows.find((r) => r.isEditing);
        if (currentlyEditingRow && currentlyEditingRow.id !== rowId) {
            const latestRow = pricingRows[pricingRows.length - 1];
            if (currentlyEditingRow.id === latestRow.id) {
                setValue(
                    "pricingRows",
                    pricingRows
                        .filter((r) => r.id !== latestRow.id)
                        .map((r) => ({ ...r, isEditing: r.id === rowId }))
                );
                return;
            }
        }
        setValue(
            "pricingRows",
            pricingRows.map((r) => ({ ...r, isEditing: r.id === rowId }))
        );
    };

    const handleExpressionType = (rowId: number, type: "value" | "operand") => {
        setValue(
            "pricingRows",
            pricingRows.map((r) =>
                r.id === rowId
                    ? { ...r, expressionType: type, dataType2: "", attribute2: "", expressionValue: "" }
                    : r
            )
        );
    };

    return (
        <div className="border bg-white border-gray-200 rounded-lg p-2">
            <div className="flex items-center justify-between border-b pb-2 mb-1">
                <h2 className="text-lg font-semibold">Pricing Rules Adjustment</h2>
            </div>

            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="bg-gray-100">
                        <th className="border border-gray-300 p-2 text-left font-medium">Data Type</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Attribute</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Operator</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Expression</th>
                        <th className="border border-gray-300 p-2 text-left font-medium">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {pricingRows.map((row) => (
                        <tr key={row.id}>
                            <td className="border border-gray-300 p-1 text-sm">{row.dataType}</td>
                            <td className="border border-gray-300 p-1 text-sm">{row.attribute}</td>
                            <td className="border border-gray-300 p-1 text-sm">=</td>

                            <td className="border border-gray-300 p-2">
                                {row.isEditing ? (
                                    <div className="flex items-center gap-2">
                                        {row.expressionType === "value" ? (
                                            <Input
                                                value={row.expressionValue || ""}
                                                onChange={(e) =>
                                                    handlePricingChange(row.id, "expressionValue", e.target.value)
                                                }
                                                className="flex-1"
                                                placeholder="Enter value"
                                            />
                                        ) : (
                                            <>
                                                {/* Data Type select */}
                                                <Select
                                                    value={row.dataType2 || ""}
                                                    onValueChange={(val) => {
                                                        const selected = dataType.find((opt) => opt.Name === val);
                                                        if (selected) handlePricingChange(row.id, "dataType2", selected);
                                                    }}
                                                >
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue placeholder="Select DataType" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {dataType.map((opt) => (
                                                            <SelectItem key={opt.Name} value={opt.Name}>
                                                                {opt.Name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>

                                                {/* Attribute select */}
                                                <Select
                                                    value={row.attribute2 || ""}
                                                    onValueChange={(val) =>
                                                        handlePricingChange(row.id, "attribute2", val)
                                                    }
                                                    disabled={!row.dataType2 || attribute.length === 0}
                                                >
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue
                                                            placeholder={
                                                                !row.dataType2
                                                                    ? "Select DataType first"
                                                                    : attribute.length === 0
                                                                        ? "No Attributes"
                                                                        : "Select Attribute"
                                                            }
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {attribute.map((opt) => (
                                                            <SelectItem key={opt.name} value={opt.name}>
                                                                {opt.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </>
                                        )}

                                        <div className="flex gap-1">
                                            <button
                                                title={row.expressionType === "value" ? "Operand" : "Value"}
                                                onClick={() =>
                                                    handleExpressionType(
                                                        row.id,
                                                        row.expressionType === "value" ? "operand" : "value"
                                                    )
                                                }
                                            >
                                                <Repeat1 className="h-4 w-4 text-gray-700" />
                                            </button>
                                            <button
                                                title="Add More"
                                                onClick={() => {
                                                    setOpenExpression(true);
                                                    handleExpressionType(row.id, "value");
                                                    setExpressionData(row);
                                                }}
                                            >
                                                <Plus className="h-4 w-4 text-gray-700" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-sm">
                                        {row.expressionType === "value"
                                            ? row.expressionValue
                                            : `${row.dataType2}.${row.attribute2}`}
                                    </span>
                                )}
                            </td>

                            <td className="border border-gray-300 p-2">
                                {row.isEditing ? (
                                    <Button
                                        size="sm"
                                        onClick={() => handleAddPricingRow(row.id)}
                                    >
                                        Update
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleEditPricingRow(row.id)}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {addWarning && (
                <div className="text-center text-red-600 mt-2 font-medium">
                    {addWarning}
                </div>
            )}
        </div>
    );
}